import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import { ReportTemplateConfigFormData } from 'dashboard/forms/FormReportTemplate';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import {
  CustomListConfigHeaderMapping,
  ReportTemplateVersionCustomList,
  ReportTemplateVersionCustomListValidateHeaderMappingsPayload,
} from 'dashboard/models/report-template-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearReportTemplate } from 'dashboard/redux/actions/report-template';
import { clearReportTemplateVersion } from 'dashboard/redux/actions/report-template-version';
import { RootState } from 'dashboard/redux/store';
import {
  loadReportTemplate,
  updateReportTemplate,
} from 'dashboard/redux/thunk-actions/report-template';
import { loadReportTemplateVersion } from 'dashboard/redux/thunk-actions/report-template-version';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import DashboardReportTemplateVersionsShowView from 'dashboard/views/DashboardReportTemplateVersionsShowView';
import { ApiOptions } from 'lib/clients/ApiClient';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import usePermission from 'lib/hooks/usePermission';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = {
  reportTemplateId: string;
  reportTemplateVersionId: string;
};

const DashboardReportTemplateVersionsShowRoute = (props: PropsFromRedux) => {
  useOnMountAndUnmount(() => {
    props.clearReportTemplate();
    props.clearReportTemplateVersion();
  });

  const { reportTemplateId, reportTemplateVersionId } = useParams<Params>() as Params;

  const { loading: loadingReportTemplate } = useApiLoader({
    loader: async () => {
      await Promise.all([
        props.loadReportTemplate(reportTemplateId),
        props.loadReportTemplateVersion(reportTemplateVersionId),
      ]);
    },
    deps: [reportTemplateId, reportTemplateVersionId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load template.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleSaveReportTemplate, error: saveReportTemplateError } = useApiCallback(
    async (values: ReportTemplateConfigFormData, options?: ApiOptions) => {
      await props.updateReportTemplate(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        props.reportTemplate.type,
        reportTemplateId,
        values,
        options,
      );
    },
    [props.updateReportTemplate, reportTemplateId, props.reportTemplate],
  );

  useApiErrorHandler({
    error: saveReportTemplateError,
    onNetworkError: () =>
      props.showMessage('Failed to save template. Please try again.', MessageType.Failure),
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          (error.errors[0]?.details as string) ?? 'Failed to save template. Please try again.',
          MessageType.Failure,
        );
      }
    },
  });

  const [isHeaderMappingsValidated, setIsHeaderMappingsValidated] = useState(false);

  const { callback: handleValidateHeaderMappings, error: validateHeaderMappingsError } =
    useApiCallback(
      async (
        headerMappings: Array<CustomListConfigHeaderMapping>,
        customListFile: FileWithPreview,
      ) => {
        await SessionClientDashboard.postResourceAction<
          ReportTemplateVersionCustomList,
          ReportTemplateVersionCustomListValidateHeaderMappingsPayload
        >(
          {
            id: reportTemplateVersionId,
            type: ApiModel.ReportTemplateVersionCustomList,
            attributes: {
              'header-mappings': headerMappings,
              'custom-list-file': customListFile,
            },
          },
          'validate-header-mappings',
          { contentType: 'multipart/form-data' },
        );

        handleUpdateMatchRequirementProfiles(headerMappings);
        setIsHeaderMappingsValidated(true);
      },
      [reportTemplateVersionId],
    );

  const [matchRequirementProfiles, setMatchRequirementProfiles] = useState<
    MatchRequirementProfile[]
  >([]);

  const { callback: handleUpdateMatchRequirementProfiles } = useApiCallback(
    async (headerMappings: Array<CustomListConfigHeaderMapping>) => {
      const matchRequirementProfiles = await SessionClientDashboard.get<MatchRequirementProfile[]>(
        ApiEndpoint.ReportTemplateVersionCustomList,
        reportTemplateVersionId,
        'match-requirement-profiles',
      );

      // Only keep match requirement profiles for the attributes that are mapped in the headerMappings
      const mappedFields = headerMappings.map((mapping) => mapping['mapped-field']);
      const filteredMatchRequirementProfiles = matchRequirementProfiles.filter((profile) =>
        mappedFields.includes(profile['attribute-name']),
      );

      setMatchRequirementProfiles(filteredMatchRequirementProfiles);
    },
    [reportTemplateVersionId],
  );

  const canEdit = usePermission(Permission.ReportTemplateWrite);

  return (
    <DashboardReportTemplateVersionsShowView
      isLoading={loadingReportTemplate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportTemplate={props.reportTemplate}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportTemplateVersion={props.reportTemplateVersion}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      latestPublishedVersion={props.latestPublishedVersion}
      matchRequirementProfiles={matchRequirementProfiles}
      canEdit={canEdit}
      saveReportTemplateError={saveReportTemplateError}
      isHeaderMappingsValidated={isHeaderMappingsValidated}
      validateHeaderMappingsError={validateHeaderMappingsError}
      handleUpdateMatchRequirementProfiles={handleUpdateMatchRequirementProfiles}
      handleSaveReportTemplate={handleSaveReportTemplate}
      handleValidateHeaderMappings={handleValidateHeaderMappings}
      setIsHeaderMappingsValidated={setIsHeaderMappingsValidated}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    reportTemplate: state.reportTemplate.reportTemplate,
    reportTemplateVersion: state.reportTemplateVersion.reportTemplateVersion,
    latestPublishedVersion: state.reportTemplate.latestPublishedVersion,
    organization: state.organization.organization,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    featureFlags: state.featureFlags.featureFlags,
  };
};

const mapDispatchToProps = {
  clearReportTemplate,
  clearReportTemplateVersion,
  loadReportTemplate,
  loadReportTemplateVersion,
  loadUserList,
  showMessage,
  updateReportTemplate,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardReportTemplateVersionsShowRoute);
